import React, { useState, createRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Swiper from 'swiper';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';
import { Tabs as TabsMIU, Tab, AppBar as AppBarMUI } from '@material-ui/core';
import dashboardColors from '../../helpers/dashboardColors';
import devices from '../../styles/devices';

const SubTitle = styled.p`
  color: ${({ theme }) => theme.colors.gray400};
  display: block;
  font-size: 1rem;
  font-weight: 600;

  @media (min-width: ${devices.tablet}) {
    display: none;
  }
`;
const Container = styled.div`
  flex-grow: 1;
  background: transparent;
  margin-top: 34px;

  .is-mobile {
    background: ${({ theme }) => theme.colors.white};
    ${SubTitle} {
      display: none !important;
    }
  }

  .MuiBox-root {
    padding: 0;
  }
  .MuiTabs-flexContainer {
    justify-content: flex-end;
  }
  .MuiAppBar-colorPrimary {
    background: transparent;
    box-shadow: none;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
  }
  .MuiTab-root {
    min-width: 80px;
    text-transform: none;
    color: ${({ theme }) => theme.colors.gray400};
    font-size: 0.9375rem;
    font-weight: 500;
  }
  .MuiTabs-indicator {
    background: ${({ theme, type }) =>
      dashboardColors(theme, type).color3} !important;
    height: 3px;
  }
  .MuiTab-textColorInherit {
    opacity: 0.5;
  }
`;

const Title = styled.h1`
  font-size: 1.625rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray400};
  text-transform: capitalize;
  flex: 1;
  margin: 0;
`;

const AppBar = styled(AppBarMUI)`
  display: none !important;

  &.is-mobile {
    display: flex !important;
    ${Title} {
      display: none !important;
    }
    .MuiTabs-root {
      width: 100%;
      .MuiTabs-flexContainer {
        justify-content: space-between;
        button {
          color: ${({ theme }) => theme.colors.white} !important;
        }
      }
      .MuiTabs-indicator {
        background: ${({ theme }) => theme.colors.white} !important;
      }
    }
  }

  @media (min-width: ${devices.tablet}) {
    display: flex !important;
  }
`;
const AppBarMobile = styled(AppBarMUI)`
  display: flex !important;
  padding: 0;
  margin-bottom: 10px;

  &.is-mobile {
    background: ${({ theme }) => theme.colors.white};
    padding: 42px 21px;
    margin-bottom: 0;
    display: none !important;
  }

  @media (min-width: ${devices.tablet}) {
    display: none !important;
  }
`;

export default function Tabs({
  type,
  title,
  labels,
  data,
  component,
  onChange,
  mobile,
}) {
  const [value, setValue] = useState(0);
  const [swiper, setSwiper] = useState();
  const swiperEl = createRef();
  const id = useMemo(() => uuid(), []);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    onChange(newValue);
    if (swiper) {
      swiper.slideTo(newValue);
    }
  };

  useEffect(() => {
    if (!swiper) {
      setSwiper(
        new Swiper(`.swiper-transactions-container-${id}`, {
          direction: 'horizontal',
          loop: false,
          allowTouchMove: false,
        }),
      );
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Container type={type}>
      <AppBar position="static" className={`${mobile ? 'is-mobile' : ''}`}>
        <Title>{title}</Title>
        {labels?.length && (
          <TabsMIU value={value} onChange={handleChange}>
            {labels?.map(label => (
              <Tab
                key={label}
                label={label}
                aria-label={`Mostrar extrato para ${label}`}
              />
            ))}
          </TabsMIU>
        )}
        {component}
      </AppBar>
      <AppBarMobile
        position="static"
        className={`${mobile ? 'is-mobile' : ''}`}
      >
        <Title>{title}</Title>
        <SubTitle>Últimas transações</SubTitle>
      </AppBarMobile>
      <div
        className={`swiper-transactions-container-${id} ${
          mobile ? 'is-mobile' : ''
        }`}
        ref={swiperEl}
        style={{
          overflow: 'hidden',
        }}
      >
        <div className="swiper-wrapper">
          {data?.map(item => (
            <div className="swiper-slide" key={item.id}>
              {item.content}
            </div>
          ))}
        </div>
      </div>
    </Container>
  );
}

Tabs.propTypes = {
  type: PropTypes.any,
  title: PropTypes.string,
  labels: PropTypes.array,
  data: PropTypes.array,
  component: PropTypes.element,
  onChange: PropTypes.func,
  mobile: PropTypes.bool,
};
